import { useEffect, useState, type FormEvent } from "react";
import type { ProductDetail } from "@shared/schemas/product";
import type { ScenarioDetail, VisibilityName } from "@shared/schemas/scenario";
import { Button } from "@client/components/Button";
import { SelectField, TextField } from "@client/components/Field";
import { Modal } from "@client/components/Modal";
import { useCreateScenario } from "@client/hooks/useScenarios";
import { describeError } from "@client/lib/errorMessage";

type Source = "baseline" | "blank";

const VISIBILITY_OPTIONS: { value: VisibilityName; label: string; hint: string }[] = [
  {
    value: "PRIVATE",
    label: "Private",
    hint: "Only you, the product owners and administrators can read it.",
  },
  {
    value: "PRODUCT",
    label: "Product-wide",
    hint: "Anyone who can see this product can read it.",
  },
];

/**
 * Fork a scenario, which is how most people start.
 *
 * "Baseline" is sent as the keyword rather than the id the page happens to hold:
 * the owner may have crowned a different baseline since this list loaded, and
 * the fork should follow the product, not a stale pointer.
 */
export function NewScenarioDialog({
  product,
  open,
  onClose,
  onCreated,
}: {
  product: ProductDetail;
  open: boolean;
  onClose: () => void;
  onCreated: (scenario: ScenarioDetail) => void;
}) {
  const create = useCreateScenario(product.id);
  const canFork = product.baselineScenarioId !== null;

  const [name, setName] = useState("");
  const [source, setSource] = useState<Source>(canFork ? "baseline" : "blank");
  const [visibility, setVisibility] = useState<VisibilityName>("PRIVATE");

  useEffect(() => {
    if (!open) return;
    setName(`${product.name} — my scenario`);
    setSource(canFork ? "baseline" : "blank");
    setVisibility("PRIVATE");
    create.reset();
    // `create` is a stable mutation object; listing it would reset the form on
    // every status change, including the one that follows a successful save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product.name, canFork]);

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (create.isPending) return;
    create.mutate(
      {
        name: name.trim(),
        visibility,
        ...(source === "baseline" ? { forkFrom: "baseline" as const } : {}),
      },
      { onSuccess: onCreated },
    );
  }

  return (
    <Modal
      open={open}
      title="New scenario"
      description={`Priced in ${product.currencyCode}, over ${product.defaultHorizonYears} years — the product's defaults.`}
      onClose={onClose}
    >
      <form className="flex flex-col gap-4" onSubmit={onSubmit}>
        <TextField
          label="Name"
          required
          maxLength={120}
          value={name}
          onChange={(event) => setName(event.target.value)}
          hint="Yours to change later. Name it after the question you are asking."
        />

        <SelectField
          label="Start from"
          value={source}
          onChange={(event) => setSource(event.target.value as Source)}
          hint={
            source === "baseline"
              ? "A copy of the product's baseline: its packages, costs and adoption, yours to edit."
              : "One empty package and no line items."
          }
        >
          <option value="baseline" disabled={!canFork}>
            The baseline{canFork ? "" : " (this product has none)"}
          </option>
          <option value="blank">An empty scenario</option>
        </SelectField>

        <SelectField
          label="Visibility"
          value={visibility}
          onChange={(event) => setVisibility(event.target.value as VisibilityName)}
          hint={VISIBILITY_OPTIONS.find((option) => option.value === visibility)?.hint}
        >
          {VISIBILITY_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </SelectField>

        {create.isError && (
          <p role="alert" className="text-sm font-semibold" style={{ color: "var(--chart-crit)" }}>
            {describeError(create.error)}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending || name.trim().length === 0}>
            {create.isPending ? "Creating…" : "Create scenario"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
